import { useState } from "react";
import { toast } from "sonner";

import { ConfirmDialog } from "~/components/confirm-dialog";
import { FilterSelect } from "~/components/filter-controls";
import { Pill } from "~/components/status-badge";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Skeleton } from "~/components/ui/skeleton";
import { Switch } from "~/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { errorMessage, isBelowCost, isConflict } from "~/lib/api";
import { EM_DASH, formatDate, formatDateTime, orDash } from "~/lib/format";
import {
  PAYMENT_METHODS,
  useCloseFeeSchedule,
  useCreateFeeSchedule,
  useFeeSchedules,
  type FeeSchedule,
  type PaymentMethod,
} from "~/lib/queries";
import { describeRate, todayInJakarta, wibMidnight } from "./-fee-format";
import { EMPTY_RATE, RateFields, toRateInput, type RateDraft } from "./-rate-fields";

/**
 * A merchant's pricing (PRO-1200): what Kugie charges them per payment method,
 * window by window.
 *
 * There is no edit. A rate that has priced a settlement is referenced from the
 * ledger, so changing pricing is "close the open window, open the next" --
 * the screen says so, rather than offering a pencil the API would refuse.
 */
export function FeeSchedulesTab({ merchantId, merchantName }: { merchantId: string; merchantName: string }) {
  const { data, error, isPending, refetch } = useFeeSchedules(merchantId);

  if (isPending) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Couldn't load fee schedules</CardTitle>
          <CardDescription>{errorMessage(error)}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            Try again
          </Button>
        </CardContent>
      </Card>
    );
  }

  const now = Date.now();
  const current = data.filter((s) => !s.effective_to || Date.parse(s.effective_to) > now);
  const ended = data.filter((s) => s.effective_to && Date.parse(s.effective_to) <= now);
  const uncovered = PAYMENT_METHODS.filter((m) => !current.some((s) => s.payment_method === m && s.in_force));

  return (
    <div className="space-y-4">
      {current.length === 0 ? (
        <p className="rounded-md border border-warning/30 bg-warning/10 p-3 text-sm">
          <strong className="font-medium">No fee schedule in force.</strong> Any settlement for{" "}
          {merchantName} will be refused until one exists. Add one below.
        </p>
      ) : null}

      <CreateCard merchantId={merchantId} merchantName={merchantName} suggestedMethod={uncovered[0]} />

      <ScheduleTable
        title="In force and upcoming"
        description="Prices every settlement whose date falls inside the window. To change a rate, close the open window and open the next one."
        schedules={current}
        merchantId={merchantId}
        closable
      />

      <ScheduleTable
        title="Ended"
        description="Kept forever: every settled payment records which of these priced it."
        schedules={ended}
        merchantId={merchantId}
        closable={false}
      />
    </div>
  );
}

function CreateCard({
  merchantId,
  merchantName,
  suggestedMethod,
}: {
  merchantId: string;
  merchantName: string;
  suggestedMethod?: PaymentMethod;
}) {
  const [method, setMethod] = useState<PaymentMethod>(suggestedMethod ?? "QRIS");
  const [from, setFrom] = useState(todayInJakarta());
  const [to, setTo] = useState("");
  const [draft, setDraft] = useState<RateDraft>(EMPTY_RATE);
  const [needsOverride, setNeedsOverride] = useState(false);
  const [override, setOverride] = useState(false);
  const [reason, setReason] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const create = useCreateFeeSchedule(merchantId);

  function reset() {
    setDraft(EMPTY_RATE);
    setTo("");
    setNeedsOverride(false);
    setOverride(false);
    setReason("");
  }

  function submit() {
    setFormError(null);
    const parsed = toRateInput(method, draft);
    if ("error" in parsed) {
      setFormError(parsed.error);
      return;
    }
    if (!from) {
      setFormError("Pick the date the window starts.");
      return;
    }
    create.mutate(
      {
        ...parsed.rate,
        effective_from: wibMidnight(from),
        effective_to: to ? wibMidnight(to) : undefined,
        below_cost_override: override || undefined,
        below_cost_reason: override ? reason.trim() : undefined,
      },
      {
        onSuccess: (created) => {
          toast.success(`${created.payment_method} schedule opened for ${merchantName}.`);
          reset();
        },
        onError: (err) => {
          if (isBelowCost(err)) setNeedsOverride(true);
        },
      },
    );
  }

  const error = formError
    ? formError
    : create.error && !isBelowCost(create.error)
      ? isConflict(create.error)
        ? `${errorMessage(create.error)}. Close the open ${method} window first, or start this one after it ends.`
        : errorMessage(create.error)
      : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Open a pricing window</CardTitle>
        <CardDescription>
          Dates are midnight Jakarta time. A start in the past is fine and is how a schedule is
          backdated to cover payments already waiting to settle; it cannot reprice anything
          already settled.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
          className="space-y-4"
        >
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1.5">
              <span className="block text-xs font-medium text-muted-foreground">Method</span>
              <FilterSelect
                value={method}
                onValueChange={(next) => next && setMethod(next as PaymentMethod)}
                options={PAYMENT_METHODS.map((m) => ({ value: m, label: m }))}
                placeholder="Method"
                className="w-48"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="fee-from" className="text-xs text-muted-foreground">
                From
              </Label>
              <Input id="fee-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="fee-to" className="text-xs text-muted-foreground">
                Until (optional)
              </Label>
              <Input id="fee-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-40" />
            </div>
          </div>

          <RateFields idPrefix="fee-create" draft={draft} onChange={setDraft} />

          {needsOverride ? (
            <div className="space-y-3 rounded-md border border-warning/30 bg-warning/10 p-3 text-sm">
              <p>
                <strong className="font-medium">This rate is below what DurianPay charges Kugie.</strong>{" "}
                Kugie would lose money on every {method} payment from {merchantName}. Save it anyway only
                if that is the deal.
              </p>
              <div className="flex items-center gap-2">
                <Switch id="fee-override" checked={override} onCheckedChange={(next) => setOverride(next)} />
                <Label htmlFor="fee-override">I mean it: save below cost</Label>
              </div>
              {override ? (
                <div className="space-y-1.5">
                  <Label htmlFor="fee-reason" className="text-xs text-muted-foreground">
                    Reason (kept on the schedule and in the audit log)
                  </Label>
                  <Input
                    id="fee-reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="e.g. launch promo agreed with the merchant until December"
                  />
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="flex items-center gap-3">
            <Button
              type="submit"
              size="sm"
              disabled={create.isPending || (needsOverride && (!override || !reason.trim()))}
            >
              {create.isPending ? "Saving…" : "Open window"}
            </Button>
            {error ? <span className="text-xs text-destructive">{error}</span> : null}
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function ScheduleTable({
  title,
  description,
  schedules,
  merchantId,
  closable,
}: {
  title: string;
  description: string;
  schedules: FeeSchedule[];
  merchantId: string;
  closable: boolean;
}) {
  return (
    <Card className="overflow-hidden">
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="px-0 pb-0">
        {schedules.length === 0 ? (
          <p className="px-6 pb-6 text-sm text-muted-foreground">Nothing here.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="pl-6">Method</TableHead>
                <TableHead>Window</TableHead>
                <TableHead>Merchant pays</TableHead>
                <TableHead>DurianPay cost</TableHead>
                <TableHead>Opened by</TableHead>
                <TableHead className="pr-6" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {schedules.map((schedule) => (
                <ScheduleRow
                  key={schedule.fee_schedule_id}
                  schedule={schedule}
                  merchantId={merchantId}
                  closable={closable && !schedule.effective_to}
                />
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function ScheduleRow({
  schedule,
  merchantId,
  closable,
}: {
  schedule: FeeSchedule;
  merchantId: string;
  closable: boolean;
}) {
  return (
    <TableRow>
      <TableCell className="pl-6 font-medium">
        <span className="flex items-center gap-2">
          {schedule.payment_method}
          {schedule.in_force ? <Pill tone="success">in force</Pill> : null}
        </span>
      </TableCell>
      <TableCell className="text-muted-foreground" title={`${formatDateTime(schedule.effective_from)} → ${schedule.effective_to ? formatDateTime(schedule.effective_to) : "open-ended"}`}>
        {formatDate(schedule.effective_from)} → {schedule.effective_to ? formatDate(schedule.effective_to) : "open-ended"}
      </TableCell>
      <TableCell className="tabular-nums">
        <span className="flex items-center gap-2">
          {describeRate(schedule.percentage_bps, schedule.fixed)}
          {schedule.below_cost ? (
            <Pill tone="danger" title={schedule.below_cost_reason ?? undefined}>
              below cost
            </Pill>
          ) : null}
        </span>
      </TableCell>
      <TableCell className="tabular-nums text-muted-foreground">
        {describeRate(schedule.cost_percentage_bps, schedule.cost_fixed)}
      </TableCell>
      <TableCell className="text-muted-foreground" title={formatDateTime(schedule.created_at)}>
        {orDash(schedule.created_by_email)}
      </TableCell>
      <TableCell className="pr-6 text-right">
        {closable ? <CloseAction schedule={schedule} merchantId={merchantId} /> : schedule.closed_at ? (
          <span className="text-xs text-muted-foreground" title={formatDateTime(schedule.closed_at)}>
            closed
          </span>
        ) : (
          EM_DASH
        )}
      </TableCell>
    </TableRow>
  );
}

/**
 * Ends an open window. Only to a future date: a past end would change which
 * schedule was in force for settlements already received -- the one edit the
 * fee table exists to make impossible -- and the API refuses it.
 */
function CloseAction({ schedule, merchantId }: { schedule: FeeSchedule; merchantId: string }) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(todayInJakarta(1));
  const close = useCloseFeeSchedule(merchantId);

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} disabled={close.isPending}>
        Close
      </Button>
      <ConfirmDialog
        open={open}
        onOpenChange={setOpen}
        title={`Close the ${schedule.payment_method} window?`}
        description={`${describeRate(schedule.percentage_bps, schedule.fixed)}, from ${formatDate(schedule.effective_from)}.`}
        confirmLabel="Close window"
        pending={close.isPending}
        onConfirm={() =>
          close.mutate(
            { feeScheduleId: schedule.fee_schedule_id, effectiveTo: wibMidnight(date) },
            {
              onSuccess: () => {
                setOpen(false);
                toast.success(`${schedule.payment_method} window closes ${date}.`);
              },
            },
          )
        }
        body={
          <>
            <div className="space-y-1.5">
              <Label htmlFor={`close-${schedule.fee_schedule_id}`} className="text-xs text-muted-foreground">
                Ends at the start of (midnight Jakarta)
              </Label>
              <Input
                id={`close-${schedule.fee_schedule_id}`}
                type="date"
                min={todayInJakarta(1)}
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-40"
              />
            </div>
            <p className="rounded-md border border-warning/30 bg-warning/10 p-2.5 text-xs">
              From that date nothing prices {schedule.payment_method} for this merchant until another
              window opens, and its settlements will be refused. Open the next window to start on the
              same date.
            </p>
            {close.error ? <p className="text-xs text-destructive">{errorMessage(close.error)}</p> : null}
          </>
        }
      />
    </>
  );
}
